import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import {
  ALL_MODES,
  DEFAULT_MODES,
  normalizeModes,
  toggleMode,
  selectAllModes,
} from '@/map/filters/modeFilter.js'
import { readViewport, writeViewport } from '@/map/viewport/viewportStorage.js'
import { prunePulses, pushPulse } from '@/map/pulses/pulseBuffer.js'
import { DEFAULT_SORT, normalizePanelModes } from '@/map/rail/corridorRanking.js'

/**
 * Map module state (spec §3, §4.2, §5.1).
 *
 * Deliberately separate from useScenarioStore: this holds view state the player
 * owns (what the map is showing, how it is arranged), not game state fetched from
 * the backend. Keeping it here is what lets a player navigate to Finance and back
 * and find the map where they left it.
 *
 * Rail state, the demand overlay and the mode filter persist globally — they are
 * habits, not per-game facts. The viewport persists per scenario; see below.
 */
export const useMapStore = defineStore('map', () => {
  // ── Rail ───────────────────────────────────────────────────────────────────
  const railOpen = useLocalStorage('tw:map:railOpen', true)

  function setRailOpen(open) { railOpen.value = !!open }
  function toggleRail()      { railOpen.value = !railOpen.value }

  // ── Demand overlay and mode filter ─────────────────────────────────────────
  const showDemandOverlay = useLocalStorage('tw:map:demandOverlay', false)

  // Stored raw, exposed normalised: a hand-edited or stale localStorage value can
  // never put the layer into a state with no active mode.
  const storedDemandModes = useLocalStorage('tw:map:demandModes', [...DEFAULT_MODES])
  const demandModes = computed(() => normalizeModes(storedDemandModes.value))
  const allDemandModesActive = computed(() => demandModes.value.length === ALL_MODES.length)

  function toggleDemandOverlay()   { showDemandOverlay.value = !showDemandOverlay.value }
  function toggleDemandMode(mode)  { storedDemandModes.value = toggleMode(storedDemandModes.value, mode) }
  function setAllDemandModes()     { storedDemandModes.value = selectAllModes() }

  // ── Viewport ───────────────────────────────────────────────────────────────
  // Keyed by scenario, unlike the three above: rail state, the overlay toggle and
  // the mode filter are cross-scenario habits, but a viewport belongs to one
  // game's home node and region. A global key would restore another scenario's
  // centre on open and never recover from it.
  const viewportByScenario = useLocalStorage('tw:map:viewportByScenario', {})

  /** @returns {{lat: number|null, lon: number|null, zoom: number|null}} */
  function viewportFor(scenarioId) {
    return readViewport(viewportByScenario.value, scenarioId)
  }

  function saveViewport(scenarioId, viewport) {
    viewportByScenario.value = writeViewport(viewportByScenario.value, scenarioId, viewport)
  }

  // ── Occlusion ──────────────────────────────────────────────────────────────
  // Zero while the rail is docked beside the map (spec §4.2). The setter exists
  // for the deferred narrow-viewport bottom sheet, which does overlay the map.
  const occludedRight = ref(0)

  function setOccludedRight(px) {
    occludedRight.value = Number.isFinite(px) && px > 0 ? px : 0
  }

  // ── Corridor inspector selection (issue 434, spec §7.1) ─────────────────────
  // Keyed by scenario like the viewport, but deliberately NOT persisted: this is
  // transient in-progress UI state (which node pair the player is mid-selecting),
  // not a durable preference. Surviving a reload as stale state would be wrong.
  const selectedCorridorPairByScenario = ref({})

  /** @returns {{originNodeId: string, destinationNodeId: string|null}|null} */
  function selectedCorridorPairFor(scenarioId) {
    return scenarioId ? (selectedCorridorPairByScenario.value[scenarioId] ?? null) : null
  }

  /**
   * Pure transition function for the corridor-pair selection state machine —
   * factored out so it is unit-testable without touching Pinia/Leaflet.
   * @param {{originNodeId: string, destinationNodeId: string|null}|null} current
   * @param {string} nodeId
   * @returns {{originNodeId: string, destinationNodeId: string|null}|null}
   */
  function nextCorridorSelection(current, nodeId) {
    if (!current) return { originNodeId: nodeId, destinationNodeId: null }
    if (current.destinationNodeId === null) {
      if (current.originNodeId === nodeId) return null // clicked the pending origin again — deselect
      return { originNodeId: current.originNodeId, destinationNodeId: nodeId } // pair complete
    }
    return { originNodeId: nodeId, destinationNodeId: null } // pair was complete, clicking elsewhere starts fresh
  }

  function selectCorridorNode(scenarioId, nodeId) {
    if (!scenarioId) return
    const next = nextCorridorSelection(selectedCorridorPairByScenario.value[scenarioId] ?? null, nodeId)
    selectedCorridorPairByScenario.value = { ...selectedCorridorPairByScenario.value, [scenarioId]: next }

    // A completed pair is a map-click entry into the corridor level; dropping the
    // selection returns to whichever level the player came from.
    if (next?.destinationNodeId) dispatchRailNav(scenarioId, 'openFromMap')
    else if (!next) dispatchRailNav(scenarioId, 'clearSelection')
  }

  // ── Rail navigation (issue 443/444, spec §9) ───────────────────────────────
  // Two levels: the ranked list, and one corridor. Not persisted, for the same
  // reason as the selection above — "you were looking at a corridor" is
  // in-progress state, and restoring it after a reload with no live selection
  // would be stale.
  const railNavByScenario = ref({})

  const DEFAULT_RAIL_NAV = { level: 'list', from: 'map' }

  /**
   * Pure transition function for the rail's two-level navigation, factored out
   * like `nextCorridorSelection` so it is unit-testable without Pinia.
   *
   * @param {{level: 'list'|'corridor', from: 'map'|'list'}} current
   * @param {'openFromList'|'openFromMap'|'back'|'clearSelection'} action
   */
  function nextRailNav(current, action) {
    const nav = current ?? DEFAULT_RAIL_NAV

    if (action === 'openFromList') return { level: 'corridor', from: 'list' }

    if (action === 'openFromMap') {
      // `from` is sticky once it is 'list': clicking the map partway through a
      // list trip must not make the back affordance vanish under the player. The
      // list is still behind them and still the right destination.
      return { level: 'corridor', from: nav.from === 'list' ? 'list' : 'map' }
    }

    if (action === 'back') {
      // Nothing behind a map-click entry, so back is a no-op there rather than a
      // door into a list the player never came from.
      return nav.level === 'corridor' && nav.from === 'list' ? { level: 'list', from: 'map' } : nav
    }

    if (action === 'clearSelection') {
      // Deselecting inside a list trip returns to the list; deselecting a
      // map-click selection keeps the pre-existing empty-state behaviour.
      return nav.from === 'list' ? { level: 'list', from: 'map' } : nav
    }

    return nav
  }

  function railNavFor(scenarioId) {
    return (scenarioId && railNavByScenario.value[scenarioId]) || DEFAULT_RAIL_NAV
  }

  function dispatchRailNav(scenarioId, action) {
    if (!scenarioId) return
    railNavByScenario.value = {
      ...railNavByScenario.value,
      [scenarioId]: nextRailNav(railNavByScenario.value[scenarioId] ?? null, action),
    }
  }

  // ── Corridor mode tab ──────────────────────────────────────────────────────
  // Lifted out of CorridorInspector's local state: a list row carries a mode and
  // must open on that tab, and a tenant switched with v-if loses local refs on
  // every switch (issue 424).
  const selectedCorridorModeByScenario = ref({})

  function selectedCorridorModeFor(scenarioId) {
    return (scenarioId && selectedCorridorModeByScenario.value[scenarioId]) || null
  }

  function setCorridorMode(scenarioId, mode) {
    if (!scenarioId) return
    selectedCorridorModeByScenario.value = { ...selectedCorridorModeByScenario.value, [scenarioId]: mode ?? null }
  }

  // ── Ranked panel sort and filter ───────────────────────────────────────────
  // Global keys, and persisted: how a player likes their list sorted is a habit
  // that carries between games, unlike a viewport. §9 also requires both to
  // survive a drill-down and return, which they do by living here rather than in
  // the panel's local refs.
  const corridorListSort = useLocalStorage('tw:map:corridorListSort', { ...DEFAULT_SORT })
  const storedCorridorListModes = useLocalStorage('tw:map:corridorListModes', [...ALL_MODES])
  const corridorListModes = computed(() => normalizePanelModes(storedCorridorListModes.value))

  function setCorridorListSort(key) {
    const current = corridorListSort.value
    corridorListSort.value = current?.key === key
      ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' }
  }

  function toggleCorridorListMode(mode) {
    const active = corridorListModes.value
    // Unlike the map's filter, emptying this one is allowed to fall back to all
    // modes rather than being blocked — an empty table is a worse dead end than
    // a reset one.
    storedCorridorListModes.value = active.includes(mode)
      ? active.filter(m => m !== mode)
      : ALL_MODES.filter(m => active.includes(m) || m === mode)
  }

  function setAllCorridorListModes() {
    storedCorridorListModes.value = [...ALL_MODES]
  }

  /**
   * Opens a corridor straight from a list row, bypassing the map's two-click
   * pair-building machine — the row already names both endpoints and the mode.
   */
  function openCorridorFromList(scenarioId, { originNodeId, destinationNodeId, mode }) {
    if (!scenarioId) return
    selectedCorridorPairByScenario.value = {
      ...selectedCorridorPairByScenario.value,
      [scenarioId]: { originNodeId, destinationNodeId },
    }
    setCorridorMode(scenarioId, mode)
    dispatchRailNav(scenarioId, 'openFromList')
  }

  function backToList(scenarioId) {
    if (!scenarioId) return
    clearCorridorSelection(scenarioId)
    dispatchRailNav(scenarioId, 'back')
  }

  function clearCorridorSelection(scenarioId) {
    if (!scenarioId) return
    const { [scenarioId]: _, ...rest } = selectedCorridorPairByScenario.value
    selectedCorridorPairByScenario.value = rest
  }

  // ── Departure pulses (issue 442, spec §6) ──────────────────────────────────
  // Not keyed by scenario and not persisted: pulses are seconds-lived ambience,
  // and the WebSocket only delivers them for the scenario the player has open.
  // Reviving them from storage would show departures that already landed.
  const corridorPulses = ref([])

  function pushCorridorPulse(pulse) {
    const now = Date.now()
    corridorPulses.value = pushPulse(prunePulses(corridorPulses.value, now), { ...pulse, at: now })
  }

  function clearCorridorPulses() {
    corridorPulses.value = []
  }

  return {
    railOpen, setRailOpen, toggleRail,
    corridorPulses, pushCorridorPulse, clearCorridorPulses,
    showDemandOverlay, toggleDemandOverlay,
    demandModes, allDemandModesActive, toggleDemandMode, setAllDemandModes,
    viewportFor, saveViewport,
    occludedRight, setOccludedRight,
    selectedCorridorPairFor, selectCorridorNode, clearCorridorSelection,
    nextRailNav, railNavFor, dispatchRailNav, openCorridorFromList, backToList,
    selectedCorridorModeFor, setCorridorMode,
    corridorListSort, setCorridorListSort,
    corridorListModes, toggleCorridorListMode, setAllCorridorListModes,
  }
})
